import { useState } from "react";
import { CampaignOrder, OrderStatus } from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";

const activeOpenOrders = true;

export function useCampaignOpenOrders() {
  const queryClient = useQueryClient();
  const { hubUser, auth } = useHubUser();
  const [loadingPercentage, setLoadingPercentage] = useState<number | null>(null);
  const [cancelLoading, setCancelLoading] = useState<Record<string, boolean>>({});
  const { enqueueSnackbar } = useSnackbar();
  const apiHelper = useApi();

  const {
    data: orders,
    refetch: refetchOpenOrders,
    isLoading: ordersAreLoading,
  } = useQuery({
    queryKey: [QueryKeys.CAMPAIGN_OPEN_ORDERS],
    queryFn: async () => {
      if (!hubUser?.defiAddress) return [];

      return apiHelper.getWithAuth(auth, API_ENDPOINT.CAMPAIGN_OPEN_ORDERS, {}).catch(() => {
        enqueueSnackbar("Failed to fetch open orders", { variant: "error" });
        return [];
      });
    },
    enabled: !!hubUser?.defiAddress,
    refetchInterval: Infinity,
  });

  const getExchangeAccounts = async (exchangeId: string) => {
    if (!hubUser) return [];
    const exchangeAccounts = hubUser.exchangeAccounts.filter((account) => account.exchangeId === exchangeId);
    return exchangeAccounts.map((acc) => ({ id: String(acc.id), exchangeId: acc.exchangeId }));
  };

  const retrieveOrder = async (order: CampaignOrder) => {
    if (!auth.identityToken) return;
    const exchangeAccounts = await getExchangeAccounts(order.exchangeId);
    if (!exchangeAccounts.length) return;
    for (const exchangeAccount of exchangeAccounts) {
      const updatedOrder = await apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_TRADE_RETRIEVE_ORDER, {
        queryParams: {
          accountId: exchangeAccount.id,
          pairSymbol: order.pairSymbol,
          orderId: order.externalOrderId,
        },
      });
      if (updatedOrder) {
        break;
      }
    }
  };

  const fetchLastOrderStatus = async () => {
    if (!orders?.length || !auth.identityToken) return;

    const loadingStep = 100 / (orders.length + 1);
    setLoadingPercentage(0);
    await Promise.all(
      orders.map(async (order) => {
        try {
          await retrieveOrder(order);
        } catch {
          enqueueSnackbar(`Failed to fetch the latest status for order ${order.pairSymbol}`, { variant: "error" });
        } finally {
          setLoadingPercentage((prev) => (prev || 0) + loadingStep);
        }
      }),
    );
    // wait for the server to update the orders and the leaderboard
    await new Promise((resolve) => setTimeout(resolve, 1000));

    await Promise.all([
      refetchOpenOrders(),
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.LEADERBOARD],
      }),
    ]).finally(() => {
      setLoadingPercentage((prev) => (prev || 0) + loadingStep);
      setTimeout(() => setLoadingPercentage(null), 1000);
    });
  };

  const cancelOrder = async (order: CampaignOrder) => {
    if (!auth.identityToken) return;

    setCancelLoading((prev) => ({ ...prev, [order.id]: true }));

    try {
      const exchangeAccounts = await getExchangeAccounts(order.exchangeId);

      for (const exchangeAccount of exchangeAccounts) {
        const exchangeOrder = await apiHelper
          .getWithAuth(auth, API_ENDPOINT.HUB_TRADE_RETRIEVE_ORDER, {
            queryParams: {
              accountId: exchangeAccount.id,
              pairSymbol: order.pairSymbol,
              orderId: order.externalOrderId,
            },
          })
          .catch(() => null);
        if (!exchangeOrder) continue;

        if (exchangeOrder.status !== OrderStatus.CLOSED && exchangeOrder.status !== OrderStatus.CANCELED) {
          await apiHelper.deleteAuth(auth, API_ENDPOINT.HUB_TRADE_CANCEL_ORDER, {
            queryParams: {
              accountId: exchangeAccount.id,
              pairSymbol: order.pairSymbol,
              orderId: order.externalOrderId,
            },
          });
          // we need to retrieve the order again to trigger the server to update the order status
          await apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_TRADE_RETRIEVE_ORDER, {
            queryParams: {
              accountId: exchangeAccount.id,
              pairSymbol: order.pairSymbol,
              orderId: order.externalOrderId,
            },
          });
          // wait for the server to update the orders and the leaderboard
          await new Promise((resolve) => setTimeout(resolve, 1000));
          await refetchOpenOrders();
          enqueueSnackbar(`Order ${order.pairSymbol} successfully canceled`, { variant: "success" });
          return;
        }
      }
    } catch {
      enqueueSnackbar(`Failed to cancel the order ${order.pairSymbol}`, { variant: "error" });
    } finally {
      setCancelLoading((prev) => ({ ...prev, [order.id]: false }));
    }
  };

  return {
    fetchLastOrderStatus,
    orders,
    loadingPercentage,
    cancelOrder,
    cancelLoading,
    ordersAreLoading,
    activeOpenOrders,
  };
}
